import { Controller } from "@hotwired/stimulus"

// Drives the redesigned marketing navbar (shared/_navbar).
// - Desktop: hover-intent open (hover-capable pointers only), click toggle,
//   keyboard (Esc/ArrowDown).
// - Scroll: toggles `is-scrolled` on the header for the shadow state.
// - Mobile: full-screen overlay with accordions, Esc + link-click close,
//   focus move-in/restore, and body scroll lock.
export default class extends Controller {
  static targets = ["header", "item", "panel", "mobile", "accordion"]

  connect() {
    this._closeTimer = null
    this._openTimer = null
    this._hoverMql = window.matchMedia("(hover: hover)")
    this._onScroll = this._onScroll.bind(this)
    this._onDocClick = this._onDocClick.bind(this)
    this._onKeydown = this._onKeydown.bind(this)
    window.addEventListener("scroll", this._onScroll, { passive: true })
    document.addEventListener("click", this._onDocClick)
    document.addEventListener("keydown", this._onKeydown)
    this._onScroll()
  }

  disconnect() {
    window.removeEventListener("scroll", this._onScroll)
    document.removeEventListener("click", this._onDocClick)
    document.removeEventListener("keydown", this._onKeydown)
    if (this._trapHandler && this.hasMobileTarget) {
      this.mobileTarget.removeEventListener("keydown", this._trapHandler)
      this._trapHandler = null
    }
    this._clearTimers()
    this._unlockBody()
  }

  _itemFor(el) { return el.closest(".nav2-item") }

  // Hover-open (hover-capable pointers only) with a short intent delay.
  open(event) {
    if (!this._hoverMql.matches) return
    const item = this._itemFor(event.currentTarget)
    if (!item) return
    this._cancelClose()
    if (this._openTimer) clearTimeout(this._openTimer)
    this._openTimer = setTimeout(() => {
      this.itemTargets.forEach((i) => { if (i !== item) this._setOpen(i, false) })
      this._setOpen(item, true)
    }, 120)
  }

  toggle(event) {
    event.preventDefault()
    this._clearOpenTimer()
    const item = this._itemFor(event.currentTarget)
    if (!item) return
    const isOpen = item.classList.contains("is-open")
    this.itemTargets.forEach((i) => this._setOpen(i, false))
    this._setOpen(item, !isOpen)
  }

  scheduleClose() {
    this._cancelClose()
    this._clearOpenTimer()
    this._closeTimer = setTimeout(() => {
      this.itemTargets.forEach((i) => this._setOpen(i, false))
    }, 150)
  }

  cancelClose() { this._cancelClose() }
  _cancelClose() { if (this._closeTimer) { clearTimeout(this._closeTimer); this._closeTimer = null } }
  _clearOpenTimer() { if (this._openTimer) { clearTimeout(this._openTimer); this._openTimer = null } }
  _clearTimers() { this._cancelClose(); this._clearOpenTimer() }

  _setOpen(item, open) {
    item.classList.toggle("is-open", open)
    const trigger = item.querySelector(".nav2-trigger")
    if (trigger) trigger.setAttribute("aria-expanded", open ? "true" : "false")
  }

  keydown(event) {
    if (event.key === "Escape") {
      const item = this._itemFor(event.currentTarget)
      if (item) { this._setOpen(item, false); event.currentTarget.focus() }
    } else if (event.key === "ArrowDown") {
      const item = this._itemFor(event.currentTarget)
      if (item) {
        this._setOpen(item, true)
        const first = item.querySelector(".nav2-panel a, .nav2-panel button")
        if (first) { event.preventDefault(); first.focus() }
      }
    }
  }

  // Global Escape: close any open desktop panel and the mobile overlay.
  _onKeydown(event) {
    if (event.key !== "Escape") return
    this.itemTargets.forEach((i) => this._setOpen(i, false))
    if (this.hasMobileTarget && this.mobileTarget.classList.contains("is-active")) {
      this._closeMobile()
    }
  }

  _onDocClick(event) {
    if (this.element.contains(event.target)) return
    this.itemTargets.forEach((i) => this._setOpen(i, false))
  }

  _onScroll() {
    if (!this.hasHeaderTarget) return
    this.headerTarget.classList.toggle("is-scrolled", window.scrollY > 8)
  }

  // Mobile overlay
  toggleMobile(event) {
    if (this.mobileTarget.classList.contains("is-active")) {
      this._closeMobile()
    } else {
      this._openMobile(event.currentTarget)
    }
  }

  _openMobile(burger) {
    this._burger = burger || this.element.querySelector(".nav2-burger")
    this.mobileTarget.classList.add("is-active")
    this.mobileTarget.setAttribute("aria-hidden", "false")
    if (this._burger) this._burger.setAttribute("aria-expanded", "true")
    this._lockBody()
    const first = this.mobileTarget.querySelector("a, button")
    if (first) first.focus()
    if (!this._mobileClickBound) {
      this._mobileClickHandler = (e) => { if (e.target.closest("a")) this._closeMobile() }
      this.mobileTarget.addEventListener("click", this._mobileClickHandler)
      this._mobileClickBound = true
    }
    if (!this._trapHandler) {
      this._trapHandler = this._onTrapKeydown.bind(this)
      this.mobileTarget.addEventListener("keydown", this._trapHandler)
    }
  }

  _closeMobile() {
    this.mobileTarget.classList.remove("is-active")
    this.mobileTarget.setAttribute("aria-hidden", "true")
    if (this._trapHandler) {
      this.mobileTarget.removeEventListener("keydown", this._trapHandler)
      this._trapHandler = null
    }
    if (this._burger) { this._burger.setAttribute("aria-expanded", "false"); this._burger.focus() }
    this._unlockBody()
  }

  // Keep Tab focus cycling within the open mobile overlay.
  _mobileFocusables() {
    return Array.from(this.mobileTarget.querySelectorAll('a[href], button:not([disabled])'))
      .filter((el) => el.offsetParent !== null)
  }

  _onTrapKeydown(event) {
    if (event.key !== "Tab") return
    const items = this._mobileFocusables()
    if (!items.length) return
    const first = items[0]
    const last = items[items.length - 1]
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault(); last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault(); first.focus()
    }
  }

  toggleAccordion(event) {
    const head = event.currentTarget
    const acc = head.closest(".nav2-acc")
    if (!acc) return
    const open = acc.classList.toggle("is-expanded")
    head.setAttribute("aria-expanded", open ? "true" : "false")
  }

  _lockBody() { document.body.classList.add("nav-open") }
  _unlockBody() { document.body.classList.remove("nav-open") }
}
